import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FormSection, Field } from "@/components/ui/form-section";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useHepSettings, useUpdateHepSettings } from "@/hooks/use-hep-settings";
import { toast } from "sonner";

export default function HepSettingsPage() {
  const { data, isLoading } = useHepSettings();
  const updateMut = useUpdateHepSettings();

  const [form, setForm] = useState({
    enabled: true,
    capture_address: "",
    capture_password: "",
    capture_id: 2001,
    uuid_type: "call-id",
  });

  useEffect(() => {
    if (data) {
      setForm({
        enabled: data.enabled,
        capture_address: data.capture_address,
        capture_password: data.capture_password,
        capture_id: data.capture_id,
        uuid_type: data.uuid_type,
      });
    }
  }, [data]);

  const updateField = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    updateMut.mutate(form, {
      onSuccess: () => toast.success("HEP settings saved — hep.conf regenerated and res_hep reloaded"),
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="HEP / Homer Capture"
        description="Mirror SIP signaling to the HOMER monitoring stack via the Homer Encapsulation Protocol (res_hep). Capture is UDP fire-and-forget: calls are unaffected if the collector is unreachable."
      />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="max-w-2xl space-y-4">
          <FormSection title="Capture" columns={2}>
            <Field label="Enabled" htmlFor="enabled" className="sm:col-span-2" info="Master switch for HEP capture. When off, no SIP signaling is mirrored to the collector.">
              <Switch
                id="enabled"
                checked={form.enabled}
                onChange={(e) => updateField("enabled", e.target.checked)}
                label={form.enabled ? "Capture on" : "Capture off"}
              />
            </Field>
            <Field label="Capture Address" required htmlFor="capture_address" info="host:port of the HEP collector (heplify-server). Default heplify.homer.local:9060 is the Cloud Map DNS name of the HOMER deployment.">
              <Input id="capture_address" value={form.capture_address} onChange={(e) => updateField("capture_address", e.target.value)} placeholder="heplify.homer.local:9060" />
            </Field>
            <Field label="Capture ID" htmlFor="capture_id" info="Numeric ID identifying this Asterisk node in HOMER. Useful when multiple servers send to the same collector.">
              <Input id="capture_id" type="number" min={0} value={form.capture_id} onChange={(e) => updateField("capture_id", Number(e.target.value))} />
            </Field>
            <Field label="Capture Password" htmlFor="capture_password" info="HEP auth password. Leave empty unless the collector requires one (the bundled heplify-server does not).">
              <Input id="capture_password" type="password" value={form.capture_password} onChange={(e) => updateField("capture_password", e.target.value)} />
            </Field>
            <Field label="UUID Type" htmlFor="uuid_type" info="Correlation key attached to captured packets: SIP Call-ID (recommended) or Asterisk channel name.">
              <Select id="uuid_type" value={form.uuid_type} onChange={(e) => updateField("uuid_type", e.target.value)}>
                <option value="call-id">call-id</option>
                <option value="channel">channel</option>
              </Select>
            </Field>
          </FormSection>

          <div className="flex justify-end">
            <Button onClick={handleSave} loading={updateMut.isPending}>
              Save &amp; Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
